import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Close,
  Refresh,
  ArrowBack,
  Send,
} from '@mui/icons-material';
import { useAdminAuth } from '../context/AdminAuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

interface SupportTicket {
  _id: string;
  ticketNumber: string;
  userType: string;
  category: string;
  status: string;
  priority: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  description: string;
  categoryDetails?: Record<string, string>;
  messages?: { sender: 'customer' | 'admin'; senderName: string; text: string; createdAt: string }[];
  adminNotes?: { note: string; addedBy: string; addedAt: string }[];
  createdAt: string;
  updatedAt: string;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
  byCategory: Record<string, number>;
  byUserType: Record<string, number>;
}

const CATEGORY_LABELS: Record<string, string> = {
  booking_issues: 'Booking Issues',
  payment_billing: 'Payment & Billing',
  account_settings: 'Account Settings',
  safety_trust: 'Safety & Trust',
  other: 'Other',
};

const USER_TYPE_LABELS: Record<string, string> = {
  pet_owner: 'Pet Owner',
  service_provider: 'Service Provider',
};

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  closed: 'default',
};

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

interface Props {
  onClose: () => void;
}

const SupportTicketList: React.FC<Props> = ({ onClose }) => {
  const { admin } = useAdminAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterUserType, setFilterUserType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);

  const token = localStorage.getItem('admin_token');

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterUserType) params.set('userType', filterUserType);
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('status', filterStatus);
      params.set('limit', '100');

      const res = await fetch(`${API_BASE_URL}/api/admin/support/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
    setLoading(false);
  }, [token, filterUserType, filterCategory, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  const handleUpdateTicket = async (id: string, updates: Record<string, string>) => {
    setUpdating(true);
    try {
      await fetch(`${API_BASE_URL}/api/admin/support/tickets/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...updates,
          adminName: admin?.name || 'Admin',
        }),
      });
      await fetchTickets();
      await fetchStats();
      if (selectedTicket?._id === id) {
        // Refresh selected ticket
        const res = await fetch(`${API_BASE_URL}/api/admin/support/tickets/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSelectedTicket(await res.json());
      }
    } catch (err) {
      console.error('Failed to update ticket:', err);
    }
    setUpdating(false);
  };

  const handleAddNote = async () => {
    if (!adminNote.trim() || !selectedTicket) return;
    await handleUpdateTicket(selectedTicket._id, { adminNote: adminNote.trim() } as any);
    setAdminNote('');
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSendingReply(true);
    try {
      await fetch(`${API_BASE_URL}/api/admin/support/tickets/${selectedTicket._id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: replyMessage.trim(),
          adminName: admin?.name || 'Admin',
          sendEmail,
        }),
      });
      setReplyMessage('');
      await fetchTickets();
      await fetchStats();
      const res = await fetch(`${API_BASE_URL}/api/admin/support/tickets/${selectedTicket._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedTicket(await res.json());
    } catch (err) {
      console.error('Failed to send reply:', err);
    }
    setSendingReply(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onClose}><ArrowBack /></IconButton>
          <Typography variant="h5" fontWeight={700}>Support Tickets</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Refresh />} onClick={() => { fetchTickets(); fetchStats(); }}>
            Refresh
          </Button>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Box>
      </Box>

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={3}>
            <Card><CardContent>
              <Typography variant="h4" fontWeight={700}>{stats.total}</Typography>
              <Typography color="text.secondary">Total Tickets</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={3}>
            <Card><CardContent>
              <Typography variant="h4" fontWeight={700} color="warning.main">{stats.byStatus?.open || 0}</Typography>
              <Typography color="text.secondary">Open</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={3}>
            <Card><CardContent>
              <Typography variant="h4" fontWeight={700} color="info.main">{stats.byStatus?.in_progress || 0}</Typography>
              <Typography color="text.secondary">In Progress</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={3}>
            <Card><CardContent>
              <Typography variant="h4" fontWeight={700} color="success.main">{stats.byStatus?.resolved || 0}</Typography>
              <Typography color="text.secondary">Resolved</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>User Type</InputLabel>
          <Select value={filterUserType} label="User Type" onChange={e => setFilterUserType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pet_owner">Pet Owner</MenuItem>
            <MenuItem value="service_provider">Service Provider</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCategory} label="Category" onChange={e => setFilterCategory(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Ticket #</strong></TableCell>
                <TableCell><strong>User Type</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No support tickets found
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map(ticket => (
                  <TableRow
                    key={ticket._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <TableCell><strong>{ticket.ticketNumber}</strong></TableCell>
                    <TableCell>
                      <Chip label={USER_TYPE_LABELS[ticket.userType] || ticket.userType} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{CATEGORY_LABELS[ticket.category] || ticket.category}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ticket.subject}
                    </TableCell>
                    <TableCell>{ticket.name}</TableCell>
                    <TableCell>
                      <Chip label={ticket.status.replace('_', ' ')} size="small" color={STATUS_COLORS[ticket.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={ticket.priority} size="small" color={PRIORITY_COLORS[ticket.priority] || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onClose={() => setSelectedTicket(null)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>
                  {selectedTicket.ticketNumber} — {selectedTicket.subject}
                </Typography>
                <IconButton onClick={() => setSelectedTicket(null)}><Close /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">User Type</Typography>
                  <Typography fontWeight={600}>{USER_TYPE_LABELS[selectedTicket.userType]}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Typography fontWeight={600}>{CATEGORY_LABELS[selectedTicket.category]}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography fontWeight={600}>{selectedTicket.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography fontWeight={600}>{selectedTicket.email}</Typography>
                </Grid>
                {selectedTicket.phone && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography fontWeight={600}>{selectedTicket.phone}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Submitted</Typography>
                  <Typography fontWeight={600}>{new Date(selectedTicket.createdAt).toLocaleString()}</Typography>
                </Grid>
              </Grid>

              {/* Conversation Thread */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Conversation</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 400, overflow: 'auto', bgcolor: '#fafafa' }}>
                {(selectedTicket.messages && selectedTicket.messages.length > 0) ? (
                  selectedTicket.messages.map((msg, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start',
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '75%',
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: msg.sender === 'admin' ? '#000' : '#fff',
                          color: msg.sender === 'admin' ? '#fff' : '#333',
                          border: msg.sender === 'customer' ? '1px solid #e0e0e0' : 'none',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: msg.sender === 'admin' ? '#aaa' : '#888', display: 'block', mb: 0.5 }}>
                          {msg.senderName} — {new Date(msg.createdAt).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.text}</Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap', bgcolor: '#fff' }}>
                    {selectedTicket.description}
                  </Paper>
                )}
              </Paper>

              {/* Reply to Customer */}
              {selectedTicket.status !== 'closed' && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply to the customer..."
                    value={replyMessage}
                    onChange={e => setReplyMessage(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={<Checkbox checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} size="small" />}
                      label={<Typography variant="body2">Email notification to customer</Typography>}
                    />
                    <Button
                      variant="contained"
                      endIcon={<Send />}
                      onClick={handleSendReply}
                      disabled={!replyMessage.trim() || sendingReply}
                    >
                      {sendingReply ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {selectedTicket.categoryDetails && Object.keys(selectedTicket.categoryDetails).length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Category Details</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {Object.entries(selectedTicket.categoryDetails).map(([k, v]) => (
                      <Typography key={k} variant="body2">
                        <strong>{k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}:</strong> {v}
                      </Typography>
                    ))}
                  </Paper>
                </>
              )}

              {/* Status & Priority Update */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedTicket.status}
                    label="Status"
                    onChange={e => handleUpdateTicket(selectedTicket._id, { status: e.target.value })}
                    disabled={updating}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={selectedTicket.priority}
                    label="Priority"
                    onChange={e => handleUpdateTicket(selectedTicket._id, { priority: e.target.value })}
                    disabled={updating}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Admin Notes */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Admin Notes</Typography>
              {selectedTicket.adminNotes && selectedTicket.adminNotes.length > 0 ? (
                selectedTicket.adminNotes.map((note, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: '#f9fafb' }}>
                    <Typography variant="body2">{note.note}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {note.addedBy} — {new Date(note.addedAt).toLocaleString()}
                    </Typography>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No notes yet</Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add a note..."
                  value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                />
                <Button variant="contained" onClick={handleAddNote} disabled={!adminNote.trim() || updating}>
                  Add
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTicket(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTicketList;
